import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Video as VideoIcon, NotebookPen, CalendarCheck, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { STEPS } from "../data/courseData";
import CustomersPanel from "../components/admin/CustomersPanel";
import logoMark from "../assets/logo-mark.png";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [videos, setVideos] = useState([]);
  const [tab, setTab] = useState("clients");
  const [editing, setEditing] = useState({});

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/videos").then((r) => setVideos(r.data));
  }, []);

  async function saveVideo(step) {
    const v = editing[step] || videos.find((x) => x.step === step);
    if (!v) return;
    await api.post("/admin/videos", { step, url: v.url, title: v.title });
    const r = await api.get("/videos");
    setVideos(r.data);
    setEditing((e) => ({ ...e, [step]: undefined }));
  }

  async function doLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="relative min-h-screen px-6 py-8">
      <div className="bps-ambient" />
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={logoMark}
                alt="Back Pain Solution"
                data-testid="admin-logo-mark"
                className="w-11 h-11 rounded-2xl object-cover drop-shadow-[0_0_18px_rgba(34,211,238,0.45)] select-none"
                draggable={false}
              />
              <div>
                <div className="bps-eyebrow">Coach Dashboard</div>
                <h1 className="font-display text-3xl font-light tracking-tighter mt-1">Welcome, {user?.name?.split(" ")[0]}</h1>
              </div>
            </div>
            <button onClick={doLogout} data-testid="admin-logout" className="text-sm text-[#9AAEC6] flex items-center gap-1"><LogOut size={14}/>Sign out</button>
          </div>

          <div className="mt-6 bps-card p-1 flex gap-1">
            <button onClick={() => setTab("clients")} data-testid="admin-tab-clients" className={`flex-1 py-3 rounded-2xl text-sm font-medium ${tab === "clients" ? "bg-[#22D3EE] text-[#03101F]" : "text-white"}`}><Users size={14} className="inline mr-1" />Customers ({clients?.length ?? "·"})</button>
            <button onClick={() => setTab("videos")} data-testid="admin-tab-videos" className={`flex-1 py-3 rounded-2xl text-sm font-medium ${tab === "videos" ? "bg-[#22D3EE] text-[#03101F]" : "text-white"}`}><VideoIcon size={14} className="inline mr-1" />Videos</button>
          </div>

          {tab === "clients" && (
            <div className="mt-5" data-testid="clients-list">
              {clients === null && (
                <div className="bps-card p-6 text-center text-sm text-[#9AAEC6] inline-flex items-center justify-center gap-3 w-full" data-testid="clients-loading">
                  <span className="w-4 h-4 rounded-full border-2 border-white/10 border-t-[#22D3EE] animate-spin" />
                  Loading customers…
                </div>
              )}
              {clients !== null && <CustomersPanel clients={clients} />}
            </div>
          )}

          {tab === "videos" && (
            <div className="mt-5 space-y-3" data-testid="videos-list">
              <p className="text-sm text-[#9AAEC6]">Paste a YouTube or Vimeo <span className="font-medium">embed</span> URL for each step (e.g. <code className="bg-white/5 px-1 rounded">https://www.youtube.com/embed/...</code>).</p>
              {STEPS.map((s) => {
                const v = editing[s.step] || videos.find((x) => x.step === s.step) || { step: s.step, url: "", title: "" };
                return (
                  <div key={s.step} className="bps-card p-5 space-y-3" data-testid={`video-row-${s.step}`}>
                    <div className="bps-eyebrow" style={{ color: s.accent }}>Week {s.step} · {s.title}</div>
                    <input value={v.title || ""} onChange={(e) => setEditing((ed) => ({ ...ed, [s.step]: { ...v, title: e.target.value } }))} placeholder="Video title" data-testid={`video-title-${s.step}`} className="w-full bg-white/5 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#22D3EE]" />
                    <input value={v.url || ""} onChange={(e) => setEditing((ed) => ({ ...ed, [s.step]: { ...v, url: e.target.value } }))} placeholder="https://www.youtube.com/embed/…" data-testid={`video-url-${s.step}`} className="w-full bg-white/5 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#22D3EE]" />
                    <button onClick={() => saveVideo(s.step)} data-testid={`video-save-${s.step}`} className="px-5 py-2.5 rounded-full bg-[#22D3EE] text-[#03101F] text-sm font-medium">Save</button>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
